import PersonComparisonModel from './../../model/personComparisonModel';
import AppStateModel from './../../../../model/appStateModel';
import ContentModel from './../../../../model/contentModel';
import MainController from '../../../../controller/MainController';
import styles from './selection.module.scss';
import template from './selection.tpl';
import {gsap} from 'gsap';
import Panel from './panel';
import StepController from './../stepController';
import HelperUtil from './../../../../../core/utils/helperUtil';

// dropdown template
import ddStyle from '@/app/components/templates/dropdown/dropdown.module.scss';
import ddTemplate from './../../../../components/templates/dropdown/dropdown.tpl'
import arrow from '@/assets/icons/dropdown-grey.svg?raw';
import nextBtnTemplate from '@/app/components/templates/nextButton/nextButton.tpl'
import nextBtnStyle from '@/app/components/templates/nextButton/nextButton.module.scss'


const _DEFAULTS = {
    left: {
        id: 'left',
        name: 'Dein Name',
        gender: 'w',
        birthyear: 45,
        estimated_age: 79
    },
    right: {
        id: 'right',
        name: 'Der Vorname der 2. Person',
        gender: 'm',
        birthyear: 45,
        estimated_age: 94
    }
};

let nextButtonHandler: ((event: any) => void) | null = null;
let placeholderRemovalHandler: ((event: any) => void) | null = null;

export default class Selection {

    private data: any;
    private common: any;
    private gender_values: any[];
    private birthyear_values: number[];
    private estimate_values: number[];
    private template: string;
    private left_panel!: Panel;
    private right_panel!: Panel;
    private target!: HTMLElement;
    private next_button!: HTMLElement;
    private input_placeholders!: NodeListOf<HTMLInputElement>;

    constructor(data) {

        this.data = data;

        // clone and store defaults unless data already exists in model
        if(!PersonComparisonModel.people) PersonComparisonModel.people = JSON.parse(JSON.stringify(_DEFAULTS));

        // drop-down values
        this.common = ContentModel.instance.getCommonData();
        this.gender_values = [this.common.gender.male, this.common.gender.female];
        this.birthyear_values = HelperUtil.getInterval(this.common.limits.birthyear.min_year, this.common.limits.birthyear.max_year);
        this.estimate_values = HelperUtil.getInterval(this.common.limits.estimate.min_year, this.common.limits.estimate.max_year);

        // template
        this.template = template({

            data: this.data,
            styles: styles,
            panels: [
                {id: PersonComparisonModel.people.left.id, placeholder: PersonComparisonModel.people.left.name},
                {id: PersonComparisonModel.people.right.id, placeholder: PersonComparisonModel.people.right.name}
            ],
            dropdown: {
                icon: arrow,
                gender: ddTemplate({data: this.data, styles: ddStyle, options: this.gender_values, icon: arrow}),
                birthyear: ddTemplate({data: this.data, styles: ddStyle, options: this.birthyear_values, icon: arrow}),
                estimate: ddTemplate({data: this.data, styles: ddStyle, options: this.estimate_values, icon: arrow})
            },
            nextButton: nextBtnTemplate({data: this.data, styles: nextBtnStyle, options: {
                    "next_step": this.data.steps.selection.next_step,
                    "is_round": this.common.layout.rounded_buttons ?? false
                }})
        });


        nextButtonHandler = (event) => this.stepComplete(event);
        placeholderRemovalHandler = (event) => this.removeInputPlaceholders(event);
    }

    init() {

        this.left_panel = new Panel(this.target, PersonComparisonModel.people.left.id);
        this.right_panel = new Panel(this.target, PersonComparisonModel.people.right.id);

        this.left_panel.init();
        this.left_panel._setDefaults(this.getExistingUserDetails(PersonComparisonModel.people.left));
        this.right_panel.init();
        this.right_panel._setDefaults(PersonComparisonModel.people.right);

        if (nextButtonHandler) {
            this.next_button.addEventListener('click', nextButtonHandler);
        }

        if (placeholderRemovalHandler) {
            for(let input of this.input_placeholders) {
                input.addEventListener('click', placeholderRemovalHandler);
            }
        }

        // disable sharing for this view/step
        MainController.setSharing(false);
    }

    getExistingUserDetails (defaults) {
        /* Populate a person-entry with existing info, or fall back to static defaults */

        let p = AppStateModel;

        return {
            'id': defaults.id,
            'name': defaults.name,
            'birthyear': (p as any).birthyear ? (p as any).birthyear : defaults.birthyear,
            'estimated_age': (p as any).estimated_age ? (p as any).estimated_age : defaults.estimated_age,
            'gender': p.gender ? p.gender : defaults.gender
        }
    }

    stepComplete(event, buttonTag = 'A') {

        // to accommodate IE10, which keeps falling over backwards... (can't use css: "pointer-events: none" on children)
        let target = event.target;
        let counter = 0;
        while (target.tagName !== buttonTag) {
            counter++;

            target = target.parentElement;

            if (counter > 10) throw new Error(`Could not find expected element containing data-target. We are looking for tag of type: ${buttonTag}`);
        } // end IE10

        StepController.nextStep(target.getAttributeNode('data-target').value, this);
    }

    removeInputPlaceholders(event) {
        event.target.getAttributeNode('placeholder').value = '';
    }

    show(timeline, onComplete) {
        /*
         Currently not needed (default fade-in handled by abstractView)
         */

        if (typeof onComplete === 'function') onComplete();
    }

    hide(timeline, onComplete) {

        timeline.add([

            gsap.to(this.target, 0.3, {autoAlpha: 0, onComplete: onComplete})
        ]);
    }

    appendTo(target) {

        this.target = target;
        this.target.setAttribute('id', this.data.steps.selection.id);
        this.target.innerHTML = this.template;

        // Collect DOM references
        this.next_button = this.target.querySelectorAll('.next_button')[0] as HTMLElement;
        this.input_placeholders = this.target.querySelectorAll('.name > input');
    }
}
